import * as React from "react";
import { alpha, Box, Checkbox, Typography, useTheme } from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import dayjs from "dayjs";

import { getBusinessColorByName } from "../utils/availabilityHelpers";
import { CalendarAvailability } from "../utils/calendarUtils";
import { useAvailabilities } from "../AvailabilitiesProvider";

interface AvailabilityItemProps {
  availability: CalendarAvailability;
}

const AvailabilityItem = ({ availability }: AvailabilityItemProps) => {
  const theme = useTheme();
  const {
    selectAvailability,
    getBookingCountForItem,
    handleCheckboxChange,
    isAvailabilitySelected,
    shouldShowCheckbox,
  } = useAvailabilities();

  const colors = getBusinessColorByName(availability.item.name);

  const bookingCount = getBookingCountForItem(
    availability.dayKey,
    availability.item.id,
  );

  const totalCapacity = availability.availability.quantity || 1;
  const availableSpots = Math.max(0, totalCapacity - bookingCount);

  const showCheckbox = shouldShowCheckbox(availability);
  const isSelected = isAvailabilitySelected(
    availability.item.id,
    availability.availabilityId,
  );

  const startTime = availability.isFareharbor
    ? (availability.availability as any).start_at
    : availability.availability.start_time;

  const timeDisplay = startTime ? dayjs(startTime).format("h:mm A") : "";

  const isPrivate = availability.availability.private;
  const referralName = availability.availability.referrals?.[0]?.customer_name;

  const accent = colors.border || colors.text || theme.palette.primary.main;
  const textColor = colors.text || theme.palette.text.primary;

  const bgBase = isPrivate
    ? alpha(theme.palette.grey[900], 0.45)
    : alpha(colors.bg, 0.22);

  const hoverBg = isPrivate
    ? alpha(theme.palette.grey[900], 0.55)
    : alpha(colors.bg, 0.3);

  const ring = isSelected ? `0 0 0 2px ${alpha(accent, 0.65)}` : "none";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectAvailability(availability);
  };

  const handleCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    handleCheckboxChange(availability, e.target.checked);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        cursor: "pointer",
        userSelect: "none",

        // condensed sizing
        borderRadius: 1,
        px: 0.75,
        py: 0.45,

        backgroundColor: bgBase,
        boxShadow: ring,
        transition:
          "background-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
        "&:hover": {
          backgroundColor: hoverBg,
          boxShadow: isSelected
            ? `0 0 0 2px ${alpha(accent, 0.75)}`
            : `0 2px 6px ${alpha("#000", 0.12)}`,
        },

        // subtle left accent bar (still "calendar event")
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          borderTopLeftRadius: 4,
          borderBottomLeftRadius: 4,
          backgroundColor: isPrivate ? alpha("#fff", 0.7) : accent,
        },
      }}
    >
      {showCheckbox && (
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={handleCheckboxClick}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            p: 0,
            color: isPrivate ? "#fff" : textColor,
            "&.Mui-checked": { color: isPrivate ? "#fff" : textColor },
            "& .MuiSvgIcon-root": { fontSize: 16 },
          }}
        />
      )}

      {/* Top row: time + name (+ private icon) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          pr: showCheckbox ? 2 : 0,
          pl: 0.75, // room from accent bar
          minWidth: 0,
        }}
      >
        {!!timeDisplay && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.15,
              color: isPrivate ? "#fff" : textColor,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {timeDisplay}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 650,
            lineHeight: 1.15,
            color: isPrivate ? "#fff" : textColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
          title={availability.item.name}
        >
          {availability.item.name}
        </Typography>

        {isPrivate && (
          <LockRoundedIcon
            sx={{
              fontSize: 14,
              color: alpha("#fff", 0.9),
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {/* Optional referral line */}
      {!!referralName && (
        <Typography
          sx={{
            mt: 0.15,
            pl: 0.75,
            pr: showCheckbox ? 2 : 0,
            fontSize: 12,
            lineHeight: 1.15,
            color: isPrivate ? alpha("#fff", 0.95) : alpha(textColor, 0.9),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={referralName}
        >
          {referralName}
        </Typography>
      )}

      {/* Bottom meta: super tight */}
      <Box
        sx={{
          mt: 0.25,
          pl: 0.75,
          pr: showCheckbox ? 2 : 0,
          display: "flex",
          alignItems: "center",
          gap: 0.6,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: 1.1,
            color: isPrivate ? alpha("#fff", 0.9) : alpha(textColor, 0.85),
            fontWeight: bookingCount > 0 ? 650 : 500,
            whiteSpace: "nowrap",
          }}
        >
          {bookingCount} booked
        </Typography>

        <Box
          sx={{
            width: 3,
            height: 3,
            borderRadius: 999,
            backgroundColor: isPrivate
              ? alpha("#fff", 0.6)
              : alpha(textColor, 0.35),
            flexShrink: 0,
          }}
        />

        <Typography
          sx={{
            fontSize: 11,
            lineHeight: 1.1,
            color: isPrivate ? alpha("#fff", 0.9) : alpha(textColor, 0.85),
            whiteSpace: "nowrap",
          }}
        >
          {availableSpots} left
        </Typography>
      </Box>
    </Box>
  );
};

export default AvailabilityItem;
